// Lifts Customer Assist session state to the AuthedLayout level so the
// chat survives navigation. Previously each ticket's TicketActions
// mounted its own dialog + channel subscription — navigate away and
// the subscription died, missing live messages and notifications.
//
// Now the dialog/widget lives at the layout, ALWAYS mounted (when a
// session is active), and individual `<CustomerAssistButton>`s just
// dispatch into this context to focus the widget on the right ticket.
//
// Concurrency model — one widget at a time:
//   The DB still allows one live session per ticket (partial unique
//   index in migration 047). An agent COULD have multiple concurrent
//   sessions across multiple tickets — those rows all stay alive
//   server-side, broadcast their own Realtime channels, and persist
//   their own messages. But the floating widget only renders one at a
//   time. Switching tickets + clicking "Live assist" rebinds the widget
//   to the new session; the previously-bound session keeps running and
//   the agent can switch back by clicking the button on its ticket.
//
//   Trade-off: only the bound session gets real-time desktop
//   notifications (the channel hook is the source of those). For v1
//   this is acceptable — multi-session juggling is rare; we'll add a
//   "sessions tray" if it becomes a real pain point.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { toastFailure } from '@/lib/toast';
import { useAuth } from '@/lib/auth-guard';
import { supabase } from '@/lib/supabase';
import { postAssistEnd } from '@/lib/api';
import { useAssistChannel, type UseAssistChannelResult } from '@/lib/use-assist-channel';
import { useAssistAttention, type UseAssistAttentionResult } from '@/lib/use-assist-attention';

export type AssistPhase = 'idle' | 'setup' | 'live' | 'ended';

/** Snapshot of the session currently bound to the widget. */
export interface BoundAssistSession {
  ticketId: string;
  /** The on-ticket subject — used in the minimized pill so the agent
   *  remembers which session they're in. Optional because the
   *  trigger button passes it in for context; if missing, the widget
   *  falls back to "Customer Assist". */
  ticketSubject?: string;
  /** Populated once the start call returns (live phase onward). */
  sessionId: string | null;
  token: string | null;
  magicLink: string | null;
  agentPurpose: string | null;
  agentName: string | null;
  /** ISO timestamp at which the pg_cron expire job (migration 047)
   *  will flip this session to status='expired' if no one explicitly
   *  ended it first. Drives the widget's countdown display. */
  expiresAt: string | null;
}

interface ContextValue {
  bound: BoundAssistSession | null;
  phase: AssistPhase;
  minimized: boolean;
  /** Live channel state — null when phase !== 'live'. */
  channel: UseAssistChannelResult | null;
  /** Attention layer state (joined banner, permission, unread). Lives
   *  in the provider (not the widget) so notifications keep firing
   *  even when the widget is minimized. Null when phase !== 'live'. */
  attention: UseAssistAttentionResult | null;
  /** Epoch-ms timestamp at which the agent's end-session grace window
   *  expires (10 seconds in the future). Set when the agent clicks
   *  End; cleared on cancel (by either side) or when the timer fires.
   *  When non-null the widget renders the "Closing in N seconds"
   *  toolbar + Cancel button instead of the regular End button. */
  pendingEndAt: number | null;
  /** Start (or open existing) session for this ticket. The widget
   *  expands and runs through the setup phase if needed. */
  openForTicket(args: { ticketId: string; ticketSubject?: string }): Promise<void>;
  /** Used by the setup modal once the start call completes. The
   *  widget transitions to live phase, bound to the new session_id. */
  attachLiveSession(args: {
    sessionId: string;
    token: string;
    magicLink: string;
    agentName: string | null;
    /** ISO timestamp for the auto-expire countdown — returned by
     *  workflow 17's start action (migration 052 / workflow change). */
    expiresAt: string | null;
  }): void;
  /** Used by the setup modal to set the agent's typed purpose so it
   *  can be displayed in the widget header during live. */
  setAgentPurpose(p: string): void;
  expand(): void;
  minimize(): void;
  /** Closes the setup modal without starting a session (user backs
   *  out of the start flow). Distinct from endSession() which ends a
   *  live session server-side. */
  cancelSetup(): void;
  /** Initiates the end flow with a 10-second grace window. Broadcasts
   *  `ending` to the client (who sees a Cancel/Wait button) and
   *  schedules the real end via workflow 17 once the window closes.
   *  Either side can call `cancelEnding()` during the window to
   *  abort. Returns once the broadcast is sent — the actual end
   *  fires asynchronously after the grace window. */
  endSession(): Promise<void>;
  /** Cancels an in-flight end. Clears the local timer + broadcasts
   *  `end_cancel` so the client UI dismisses its countdown banner.
   *  No-op when no end is pending. */
  cancelEnding(): void;
  /** Skip the 10s grace window and end immediately. Wired to the
   *  "End now" button in the widget for the case where the agent
   *  is 100% sure (e.g., the client already said goodbye). */
  endSessionNow(): Promise<void>;
  dismissEnded(): void;
}

const AssistSessionContext = createContext<ContextValue | null>(null);

/** Hook callers use to interact with the global widget. Throws when
 *  used outside the provider — guards against forgetting to wrap. */
export function useAssistSession(): ContextValue {
  const ctx = useContext(AssistSessionContext);
  if (!ctx) {
    throw new Error('useAssistSession must be used inside <AssistSessionProvider>');
  }
  return ctx;
}

export function AssistSessionProvider({ children }: { children: ReactNode }) {
  const { teamMember } = useAuth();
  const agentName = teamMember?.name ?? '';
  const qc = useQueryClient();

  const [bound, setBound] = useState<BoundAssistSession | null>(null);
  const [phase, setPhase] = useState<AssistPhase>('idle');
  const [minimized, setMinimized] = useState(false);
  // Local mirror of the grace-window deadline. Drives the agent's
  // "Closing in Ns" UI. Cleared on cancel or when the timer fires.
  // We keep this in the provider (not the channel hook) because the
  // agent IS the initiator on the current code path — the channel
  // hook only tracks "the OTHER side is ending" so it can render the
  // receiving-side countdown. Mixing the two roles in one piece of
  // state would muddy who-is-cancelling-whom.
  const [pendingEndAt, setPendingEndAt] = useState<number | null>(null);
  const endingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 10 seconds — see the user-facing copy ("Closing in N seconds").
  // Pulled out so the channel + UIs stay in sync if we ever tune it.
  const END_GRACE_MS = 10_000;
  // Surfaced via toast on errors that happen inside actions; nothing
  // else for now.

  // Subscribe to the channel whenever phase is 'live' and we have a
  // session id. The hook handles cleanup on sessionId change → channel
  // tears down + rebuilds automatically when the agent switches to a
  // different ticket's session.
  const channel = useAssistChannel({
    client: supabase,
    sessionId: phase === 'live' ? bound?.sessionId : null,
    role: 'agent',
    display_name: agentName || 'Agent',
    enabled: phase === 'live' && !!bound?.sessionId,
  });

  // Attention orchestrator lives HERE (not in the widget) so it keeps
  // firing desktop notifications + toasts + tab-title flashes even
  // when the widget is minimized. `treatAsBackground: minimized` tells
  // the hook to fire notifications even with the tab focused, because
  // a minimized chat is conceptually "not being looked at" — same
  // semantics as a backgrounded tab.
  const attention = useAssistAttention({
    otherSideOnline: channel.peers.length > 0,
    messages: channel.messages,
    viewerRole: 'agent',
    enabled: phase === 'live',
    treatAsBackground: minimized,
  });

  // If the other side broadcasts 'end' (the client ended), transition
  // to 'ended' + finalize via workflow 17. Mirrors the previous in-
  // dialog logic, just lifted here so it works regardless of view.
  const endingRef = useRef(false);
  useEffect(() => {
    if (phase !== 'live') return;
    if (!channel.ended || endingRef.current) return;
    void finalizeEnd();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel.ended, phase]);

  async function finalizeEnd() {
    if (!bound?.sessionId) return;
    if (endingRef.current) return;
    endingRef.current = true;
    const transcript = buildTranscript(channel.messages, agentName || 'Agent');
    try {
      await postAssistEnd({ session_id: bound.sessionId, transcript });
    } catch (e) {
      toastFailure(e, "Couldn't update the assist session");
    }
    setPhase('ended');
    // Invalidate any caches so the ticket detail's "active assist
    // session" pill flips back to neutral immediately.
    qc.invalidateQueries({ queryKey: ['active-assist-session', bound.ticketId] });
    endingRef.current = false;
  }

  const openForTicket = useCallback(
    async ({ ticketId, ticketSubject }: { ticketId: string; ticketSubject?: string }) => {
      // Quick check for an existing pending/active session on this
      // ticket via the cached query if we have it; fall back to
      // querying directly. Either way, we want to either:
      //   1. Resume the live session that's already running (jump
      //      straight to live phase)
      //   2. Enter setup phase if there's nothing in flight.
      try {
        const { data, error } = await supabase
          .from('customer_assist_sessions')
          .select('id, token, agent_purpose, agent_name, expires_at')
          .eq('ticket_id', ticketId)
          .in('status', ['pending', 'active'])
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (error) throw error;
        if (data) {
          const magicLink = `${window.location.origin}/assist/${data.token}`;
          setBound({
            ticketId,
            ticketSubject,
            sessionId: data.id,
            token: data.token,
            magicLink,
            agentPurpose: data.agent_purpose ?? null,
            agentName: data.agent_name ?? null,
            expiresAt: data.expires_at ?? null,
          });
          setPhase('live');
          setMinimized(false);
          return;
        }
      } catch (e) {
        // Lookup failure is non-fatal — fall through to setup. The
        // partial-unique-index + workflow's pre-INSERT check would
        // catch a real concurrent-session race.
        console.warn('[assist] active-session lookup failed:', e);
      }

      // No live session → setup phase, modal opens.
      setBound({
        ticketId,
        ticketSubject,
        sessionId: null,
        token: null,
        magicLink: null,
        agentPurpose: null,
        agentName: agentName || null,
        expiresAt: null,
      });
      setPhase('setup');
      setMinimized(false);
    },
    [agentName],
  );

  const attachLiveSession = useCallback(
    (args: {
      sessionId: string;
      token: string;
      magicLink: string;
      agentName: string | null;
      expiresAt: string | null;
    }) => {
      setBound((prev) =>
        prev
          ? {
              ...prev,
              sessionId: args.sessionId,
              token: args.token,
              magicLink: args.magicLink,
              agentName: args.agentName ?? prev.agentName,
              expiresAt: args.expiresAt,
            }
          : prev,
      );
      setPhase('live');
      setMinimized(false);
    },
    [],
  );

  const setAgentPurpose = useCallback((p: string) => {
    setBound((prev) => (prev ? { ...prev, agentPurpose: p } : prev));
  }, []);

  const expand = useCallback(() => setMinimized(false), []);
  const minimize = useCallback(() => setMinimized(true), []);

  const cancelSetup = useCallback(() => {
    setPhase('idle');
    setBound(null);
    setMinimized(false);
  }, []);

  // The hard-end path — kept private; called either by the grace-
  // window timer or by `endSessionNow()`. Broadcasts a final `end`
  // event AND POSTs to workflow 17 to write the transcript + flip
  // status='ended'.
  const performHardEnd = useCallback(async () => {
    if (!bound?.sessionId) return;
    channel.sendEnd();
    setPendingEndAt(null);
    if (endingTimerRef.current) {
      clearTimeout(endingTimerRef.current);
      endingTimerRef.current = null;
    }
    await finalizeEnd();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bound?.sessionId, channel]);

  const endSession = useCallback(async () => {
    if (phase !== 'live') return;
    if (!bound?.sessionId) return;
    // Already pending — ignore double-clicks. Cancel + restart isn't
    // useful; the agent who wants to actually skip the grace should
    // hit "End now" instead.
    if (endingTimerRef.current) return;

    const endsAt = Date.now() + END_GRACE_MS;
    setPendingEndAt(endsAt);
    // Tell the client side so they can render the "Closing in Ns +
    // Wait button" banner. We DON'T also call channel.sendEnd() here
    // — the real end fires inside the timeout below.
    channel.sendEnding(END_GRACE_MS);
    // Schedule the actual end. If `cancelEnding()` fires first, the
    // timer is cleared and we never get here.
    endingTimerRef.current = setTimeout(() => {
      endingTimerRef.current = null;
      void performHardEnd();
    }, END_GRACE_MS);
  }, [phase, bound?.sessionId, channel, performHardEnd]);

  const cancelEnding = useCallback(() => {
    if (!endingTimerRef.current && pendingEndAt === null) return;
    if (endingTimerRef.current) {
      clearTimeout(endingTimerRef.current);
      endingTimerRef.current = null;
    }
    setPendingEndAt(null);
    // Tell the client (whoever's in the conversation) that the end
    // was cancelled so their banner dismisses + countdown stops.
    channel.sendEndCancel();
    toast.success('Session continues — keep chatting.');
  }, [channel, pendingEndAt]);

  const endSessionNow = useCallback(async () => {
    if (phase !== 'live') return;
    if (!bound?.sessionId) return;
    await performHardEnd();
  }, [phase, bound?.sessionId, performHardEnd]);

  // If the OTHER side broadcasts `end_cancel` during our grace
  // window, abort our pending end. We can't watch `channel.endingAt`
  // for this — the agent's own `ending` broadcast is self-suppressed
  // by the channel, so endingAt was never non-null on the agent
  // side; there's no transition to observe. Instead the channel
  // bumps `endCancelReceivedAt` every time it receives an `end_cancel`
  // payload, and we watch THAT for changes.
  const lastEndCancelReceivedAtRef = useRef<number | null>(null);
  useEffect(() => {
    const prev = lastEndCancelReceivedAtRef.current;
    lastEndCancelReceivedAtRef.current = channel.endCancelReceivedAt;
    if (channel.endCancelReceivedAt === null) return;
    if (prev === channel.endCancelReceivedAt) return;
    // The other side hit Cancel/Wait. Only matters if we currently
    // have a pending end — otherwise the broadcast is a no-op
    // (e.g., the client cancelled before we even started ending).
    if (!endingTimerRef.current && pendingEndAt === null) return;
    if (endingTimerRef.current) {
      clearTimeout(endingTimerRef.current);
      endingTimerRef.current = null;
    }
    setPendingEndAt(null);
    toast.info("Client asked to keep talking — let's continue.");
  }, [channel.endCancelReceivedAt, pendingEndAt]);

  // Cleanup: if the session unmounts or transitions away from live
  // while a timer is pending, clear it. Otherwise the timer would
  // fire against a stale session state.
  useEffect(() => {
    if (phase === 'live') return;
    if (endingTimerRef.current) {
      clearTimeout(endingTimerRef.current);
      endingTimerRef.current = null;
    }
    setPendingEndAt(null);
  }, [phase]);

  const dismissEnded = useCallback(() => {
    setPhase('idle');
    setBound(null);
    setMinimized(false);
  }, []);

  const value = useMemo<ContextValue>(
    () => ({
      bound,
      phase,
      minimized,
      channel: phase === 'live' ? channel : null,
      attention: phase === 'live' ? attention : null,
      pendingEndAt,
      openForTicket,
      attachLiveSession,
      setAgentPurpose,
      expand,
      minimize,
      cancelSetup,
      endSession,
      cancelEnding,
      endSessionNow,
      dismissEnded,
    }),
    [
      bound,
      phase,
      minimized,
      channel,
      attention,
      pendingEndAt,
      openForTicket,
      attachLiveSession,
      setAgentPurpose,
      expand,
      minimize,
      cancelSetup,
      endSession,
      cancelEnding,
      endSessionNow,
      dismissEnded,
    ],
  );

  return <AssistSessionContext.Provider value={value}>{children}</AssistSessionContext.Provider>;
}

// ── Shared transcript builder ────────────────────────────────────────────────
// Lives here (rather than in the widget) so the provider can call it
// during finalizeEnd without depending on the widget being mounted.
import type { AssistChatMessage } from '@/lib/use-assist-channel';
export function buildTranscript(messages: AssistChatMessage[], agentName: string): string {
  if (messages.length === 0) {
    return 'Customer Assist session — no chat exchanged.';
  }
  const header = `Customer Assist transcript (${new Date().toLocaleString()})`;
  const lines = messages.map((m) => {
    const who = m.from === 'agent' ? agentName : 'Client';
    const ts = new Date(m.ts).toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });
    const body = m.text || '';
    // Attachments: inline filename list only. We deliberately drop
    // the Supabase Storage URLs from the transcript — they're 150+
    // character unbreakable strings that overflow note containers, and
    // the URLs are already preserved on the `customer_assist_messages`
    // row (`attachments` jsonb) if anyone needs to retrieve them.
    const attLine = m.attachments.length > 0
      ? '  📎 ' + m.attachments.map((a) => a.filename).join(', ')
      : '';
    const main = `[${ts}] ${who}: ${body}`;
    return attLine ? `${main}\n${attLine}` : main;
  });
  return `${header}\n\n${lines.join('\n')}`;
}
